import { useEffect, useState } from 'react';
import { useSession } from '@clerk/clerk-react';
import { API_URL } from 'constants/index';

export enum MergerStep {
  VALIDATING = 'validating',
  UPDATING_DATABASE = 'updating_database',
  UPDATING_BUILDINGS = 'updating_buildings',
  UPDATING_PRODUCTS = 'updating_products',
  UPDATING_ODOO = 'updating_odoo',
  COMPLETED = 'completed',
  FAILED = 'failed',
}

export interface ContractProgress {
  contractId: number;
  odooContractId: number;
  customerName: string;
  buildingId: string;
  status: 'pending' | 'processing' | 'success' | 'failed';
  error?: string;
  priceChanges?: Array<{
    productName: string;
    oldOrderLineId: string;
    newOrderLineId?: string;
    oldPriceUnit: number;
    oldBasePrice: number;
    newPriceUnit: number;
  }>;
}

export interface MergerProgress {
  jobId: string;
  step: MergerStep;
  currentStepDescription: string;
  totalContracts: number;
  processedContracts: number;
  successfulContracts: number;
  failedContracts: number;
  contracts: ContractProgress[];
  errors: string[];
  completed: boolean;
  success: boolean;
  summary?: {
    buildingsMoved: number;
    contractsUpdated: number;
    productsUpdated: number;
    odooContractsUpdated: number;
    odooContractLinesFailed: number;
  };
}

export function useMergerProgress(jobId: string | null) {
  const { session } = useSession();
  const [progress, setProgress] = useState<MergerProgress | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    if (!jobId) {
      console.log('useMergerProgress: No jobId provided');
      return;
    }

    if (!session) {
      console.log('useMergerProgress: No session available, waiting...');
      return;
    }

    console.log('useMergerProgress: Setting up SSE connection for jobId:', jobId);

    let eventSource: EventSource | null = null;
    let reconnectAttempts = 0;
    const maxReconnectAttempts = 5;

    const connect = async () => {
      try {
        // Get session token for authentication
        const token = await session.getToken();
        if (!token || !session.id) {
          console.error('useMergerProgress: Failed to get session token or ID');
          setError('Authenticatie mislukt');
          return;
        }

        // EventSource doesn't support custom headers, so we pass auth data as query params
        const sseUrl = `${API_URL}/companies/merge/progress/${jobId}?token=${encodeURIComponent(token)}&sessionId=${encodeURIComponent(session.id)}`;
        console.log('useMergerProgress: Connecting to SSE endpoint:', `${API_URL}/companies/merge/progress/${jobId}`);
        eventSource = new EventSource(sseUrl);

        eventSource.onopen = () => {
          console.log('useMergerProgress: SSE connection established');
          setIsConnected(true);
          setError(null);
          reconnectAttempts = 0;
        };

        eventSource.onmessage = (event) => {
          console.log('useMergerProgress: Received SSE message:', event.data);
          try {
            const data = JSON.parse(event.data);
            console.log('useMergerProgress: Parsed progress data:', data);
            setProgress(data);

            // Close connection when completed
            if (data.completed) {
              console.log('useMergerProgress: Job completed, closing connection');
              eventSource?.close();
              setIsConnected(false);
            }
          } catch (err) {
            console.error('useMergerProgress: Failed to parse SSE message:', err, 'Raw data:', event.data);
          }
        };

        eventSource.onerror = (err) => {
          console.error('SSE connection error:', err);
          setIsConnected(false);
          eventSource?.close();

          // Attempt reconnection with exponential backoff
          if (reconnectAttempts < maxReconnectAttempts) {
            reconnectAttempts++;
            const delay = Math.min(1000 * Math.pow(2, reconnectAttempts), 10000);
            console.log(`Reconnecting in ${delay}ms (attempt ${reconnectAttempts}/${maxReconnectAttempts})...`);
            setTimeout(connect, delay);
          } else {
            setError('Verbinding verloren. Ververs de pagina om opnieuw te proberen.');
          }
        };
      } catch (err) {
        console.error('Failed to create EventSource:', err);
        setError('Kon geen verbinding maken met de server');
      }
    };

    connect();

    // Cleanup
    return () => {
      eventSource?.close();
      setIsConnected(false);
    };
  }, [jobId, session]);

  return {
    progress,
    error,
    isConnected
  };
}
